import axios from 'axios';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Capacitor } from '@capacitor/core';
import { installOfflineQueue } from './offlineQueue';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IS_NATIVE = Capacitor.isNativePlatform();
const TOKENS_KEY = 'fpq_auth_tokens_v1';
let tokens = null; // native only: { access_token, refresh_token }

export async function loadTokens() {
  if (!IS_NATIVE) return;
  tokens = (await SecureStorage.get(TOKENS_KEY).catch(() => null)) || null;
}

export async function saveTokens(data) {
  if (!IS_NATIVE || !data?.access_token) return;
  tokens = { access_token: data.access_token, refresh_token: data.refresh_token };
  await SecureStorage.set(TOKENS_KEY, tokens);
}

export async function clearTokens() {
  tokens = null;
  if (IS_NATIVE) await SecureStorage.remove(TOKENS_KEY).catch(() => {});
}

// Native <img>/<a> can't send headers: append the short-lived access token to authenticated file URLs.
export const authFileUrl = (path) => {
  const url = path?.startsWith('http') ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
  if (!IS_NATIVE || !tokens?.access_token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}at=${encodeURIComponent(tokens.access_token)}`;
};

const API = axios.create({
  baseURL: BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

API.interceptors.request.use((config) => {
  if (IS_NATIVE) {
    config.headers['X-Client'] = 'native';
    if (tokens?.access_token) config.headers.Authorization = `Bearer ${tokens.access_token}`;
  }
  return config;
});

let refreshing = null;
async function refreshSession() {
  const headers = IS_NATIVE ? { 'X-Client': 'native' } : {};
  const body = IS_NATIVE ? { refresh_token: tokens?.refresh_token || '' } : {};
  const { data } = await axios.post(`${BASE}/auth/refresh`, body, { withCredentials: true, headers });
  await saveTokens(data);
}

API.interceptors.response.use(
  (res) => res,
  async (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/');
    if (error.response?.status === 401 && !error.config._retry && !isAuthEndpoint) {
      error.config._retry = true;
      try {
        refreshing = refreshing || refreshSession().finally(() => { refreshing = null; });
        await refreshing;
        return API(error.config);
      } catch {
        await clearTokens();
        window.dispatchEvent(new Event('fpq:logged-out'));
      }
    }
    return Promise.reject(error);
  }
);

installOfflineQueue(API);

export default API;
